import type { App } from "vue";

import { useToast } from "@nuxt/ui/composables";

declare const __FRONTEND_BUILD_ID__: string;

type ToastHandle = ReturnType<typeof useToast>;

const BUILD_INFO_URL = "/build.json";
const CHECK_INTERVAL_MS = 60_000;

// These module-level flags keep timer ticks, visibility events, and startup checks from racing each other.
let hasUpdatePrompt = false;
let activeCheck: Promise<void> | null = null;

/** Starts stale-build detection after a Nuxt UI Vue app has mounted. */
export function startBuildRefreshCheck (app: App, appId: string) {
	if (import.meta.env.DEV) {
		return;
	}

	const toast = app.runWithContext(() => useToast());

	// Visible tabs poll the same-origin marker; hidden tabs avoid background prompting.
	window.setInterval(() => {
		if (document.visibilityState === "visible") {
			void checkForBuildChange(toast, appId);
		}
	}, CHECK_INTERVAL_MS);

	// A returning tab checks immediately instead of waiting for the next interval tick.
	document.addEventListener("visibilitychange", () => {
		if (document.visibilityState === "visible") {
			void checkForBuildChange(toast, appId);
		}
	});

	// The initial check catches mismatched HTML/assets as soon as a production page loads.
	if (document.visibilityState === "visible") {
		void checkForBuildChange(toast, appId);
	}
}

/** Compares the current deployment marker with the build id embedded in the loaded bundle. */
async function checkForBuildChange (toast: ToastHandle, appId: string) {
	if (hasUpdatePrompt || activeCheck) {
		return;
	}

	activeCheck = fetchCurrentBuildInfo()
		.then((buildInfo) => {
			if (buildInfo.buildId !== __FRONTEND_BUILD_ID__) {
				showUpdatePrompt(toast, appId);
			}
		})
		.catch(() => {
			// If the marker is temporarily unavailable, keep the current app running.
		})
		.finally(() => {
			activeCheck = null;
		});

	await activeCheck;
}

/** Fetches and validates the fresh build marker served beside the static app. */
async function fetchCurrentBuildInfo () {
	const response = await fetch(BUILD_INFO_URL, {
		cache: "no-store",
	});

	if (!response.ok) {
		throw new Error(`Unable to fetch ${BUILD_INFO_URL}`);
	}

	const buildInfo: unknown = await response.json();
	if (!isBuildInfo(buildInfo)) {
		throw new Error(`Invalid build info from ${BUILD_INFO_URL}`);
	}

	return buildInfo;
}

function isBuildInfo (value: unknown): value is { buildId: string } {
	return typeof value === "object"
		&& value !== null
		&& "buildId" in value
		&& typeof value.buildId === "string"
		&& value.buildId.length > 0;
}

/** Shows the persistent refresh prompt that is only cleared by loading the current build. */
function showUpdatePrompt (toast: ToastHandle, appId: string) {
	hasUpdatePrompt = true;

	toast.add({
		id: `khawk-${appId}-build-update`,
		title: "Update available",
		description: "Refresh to get the latest version",
		color: "info",
		close: false,
		duration: 0,
		onEscapeKeyDown (event) {
			event.preventDefault();
		},
		onSwipeEnd (event) {
			event.preventDefault();
		},
		actions: [
			{
				label: "Refresh",
				onClick () {
					// The full reload replaces stale code and drops any unsafe in-memory app state.
					window.location.reload();
				},
			},
		],
	});
}
